import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useTimeFrame } from '../contexts/TimeFrameContext';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import LicenseGate from '../components/ui/LicenseGate';
import DashboardSectionAnchor from '../components/ui/DashboardSectionAnchor';
import { useSectionHashTabSwitch } from '../hooks/useSectionHashTabSwitch';
import apiClient from '../utils/apiClient';
import { TimePeriod } from '../types/api';
import { logger } from '../utils/logger';
import {
  KPIsSection,
  OverallSatisfaction,
  ShiftSatisfaction,
  OrderTypeSatisfaction,
  DetailedSatisfaction
} from '../components/satisfaction';

interface BranchInfo {
  BranchID: number;
  BranchName: string;
}

interface SatisfactionDashboardProps {
  branchId?: number;
  timePeriod?: string | number; // For backwards compatibility
}

const SatisfactionDashboard: React.FC<SatisfactionDashboardProps> = ({ 
  branchId = 0, 
  timePeriod
}) => {
  // Theme context
  const { currentTheme, theme } = useTheme();
  
  // Time frame context
  const { timeFrame } = useTimeFrame();
  
  // Convert string period to number
  const getTimePeriodNumber = (period: string | number): number => {
    if (typeof period === 'number') return period;
    
    switch (period) {
      case 'last1Month': return 1;
      case 'last3Months': return 3;
      case 'last6Months': return 6;
      case 'lastYear': 
      case 'last12Months': return 12;
      case 'last24Months': return 24;
      default: return 6;
    }
  };

  // Use the provided timePeriod prop if available, otherwise use the timeFrame from context
  const effectiveTimePeriod = timePeriod || timeFrame;
  const timePeriodNumber = getTimePeriodNumber(effectiveTimePeriod);
  
  // Convert to TimePeriod type
  const getTimePeriodType = (period: string | number): TimePeriod => {
    const months = typeof period === 'number' ? period : getTimePeriodNumber(period);
    if (months <= 1) return '1month';
    if (months <= 3) return '3months';
    if (months <= 6) return '6months';
    return '12months';
  };
  
  const timeRangeForComponents = getTimePeriodType(effectiveTimePeriod);
  
  // State declarations
  const [branches, setBranches] = useState<BranchInfo[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<number>(branchId); // Use prop value
  
  // Update selectedBranch when branchId prop changes
  React.useEffect(() => {
    setSelectedBranch(branchId);
  }, [branchId]);

  const [branchName, setBranchName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Tab navigation state
  type TabType = 'overview' | 'shifts' | 'orderTypes' | 'trends' | 'categories';
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  useSectionHashTabSwitch(
    {
      'overall-satisfaction': 'overview',
      'shift-satisfaction': 'shifts',
      'order-type-satisfaction': 'orderTypes',
      'detailed-satisfaction': 'categories',
    },
    setActiveTab,
  );

  // Check if detailed satisfaction data is available
  const [hasDetailedData, setHasDetailedData] = useState<boolean>(false);

  // Fetch all branches
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        await apiClient.get('/brands/1');
        
        const branchesResponse = await apiClient.get('/branches/brand/1');
        setBranches(branchesResponse.data);
      } catch (err) {
        logger.error('Error fetching branches', 'SatisfactionDashboard', err);
      }
    };

    fetchBranches();
  }, []);

  // Fetch branch info
  useEffect(() => {
    const fetchBranchInfo = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch branch info
        const branchResponse = await apiClient.get(`/branches/${selectedBranch}`);
        setBranchName(branchResponse.data.BranchName);
        
        // Check if detailed satisfaction data is available
        try {
          const detailedResponse = await apiClient.get(`/detailed-satisfaction/${selectedBranch}?months=${timePeriodNumber}`);
          setHasDetailedData(detailedResponse.data.data && detailedResponse.data.data.length > 0);
        } catch (err) {
          setHasDetailedData(false);
        }
      } catch (err) {
        logger.error('Error fetching branch info', 'SatisfactionDashboard', err);
        setError('خطا در بارگذاری اطلاعات شعبه');
      } finally {
        setLoading(false);
      }
    };

    if (selectedBranch) {
      fetchBranchInfo();
    }
  }, [selectedBranch, timePeriodNumber]);

  // Handle component errors
  const handleComponentError = (error: Error) => {
    logger.error('Component error', 'SatisfactionDashboard', error);
    // Errors are handled individually by each component
  };

  // Render component
  return (
    <LicenseGate licenseType="evaluation" dashboardName="رضایت مشتریان">
      <div className="container-dashboard" dir="rtl">
        <div className="space-y-8 md:space-y-10 animate-enter-up">
          {/* Header Section */}
          {loading ? (
            <div className="flex flex-col items-center justify-center min-h-[300px]" role="status" aria-live="polite">
              <LoadingSpinner
                variant="minimal"
                size="lg"
                message="در حال بارگذاری داده‌های داشبورد…"
              />
              <p className="mt-4 text-sm content-secondary">لطفاً چند لحظه صبر کنید…</p>
            </div>
          ) : error ? (
            <div className="p-4 m-6 bg-status-error text-text-inverse rounded-lg shadow-md animate-enter" role="alert" aria-live="assertive">
              <div className="flex items-center">
                <svg className="w-6 h-6 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="font-medium">{error}</span>
              </div>
              <button className="mt-4 px-4 py-2 bg-bg-primary/20 hover:bg-bg-primary/30 text-text-inverse rounded-lg transition-colors font-medium animate-hover-lift">
                تلاش مجدد
              </button>
            </div>
          ) : (
            <>
              <h1 className="title-dashboard">
                رضایت مشتریان
              </h1>
              
              {/* Summary Cards - KPIs Section */}
              <div id="section-satisfaction-summary" className="dashboard-section-anchor">
                <KPIsSection 
                  branchId={selectedBranch}
                  timeRange={timeRangeForComponents}
                  onError={handleComponentError}
                />
              </div>

              {/* Tab Navigation */}
              <div className="card-base card-padding animate-enter animate-delay-3">
                <div className="border-b border-border-secondary">
                  <div className="flex mobile-tab-scroll lg:flex-wrap items-center justify-center -mb-px">
                    <button
                      onClick={() => setActiveTab('overview')}
                      className={`
                        relative flex items-center justify-center gap-3 px-8 py-2.5 text-base font-medium transition-all duration-300
                        ${activeTab === 'overview' 
                          ? 'bg-white dark:bg-gray-800 text-primary-600 dark:text-primary-400 border-t-2 border-r-2 border-l-2 border-border-secondary rounded-t-xl shadow-[0_-8px_16px_rgba(0,0,0,0.15),-6px_0_12px_rgba(0,0,0,0.08),6px_0_12px_rgba(0,0,0,0.08)]' 
                          : 'bg-gray-50 dark:bg-gray-900 text-text-secondary hover:text-text-primary hover:bg-gray-100 dark:hover:bg-gray-800 rounded-t-xl'}
                      `}
                    >
                      <span className="text-lg font-semibold">نمای کلی</span>
                      {activeTab === 'overview' && (
                        <div className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-white dark:bg-gray-800"></div>
                      )}
                    </button>
                    <button
                      onClick={() => setActiveTab('shifts')}
                      className={`
                        relative flex items-center justify-center gap-3 px-8 py-2.5 text-base font-medium transition-all duration-300 mr-2
                        ${activeTab === 'shifts' 
                          ? 'bg-white dark:bg-gray-800 text-primary-600 dark:text-primary-400 border-t-2 border-r-2 border-l-2 border-border-secondary rounded-t-xl shadow-[0_-8px_16px_rgba(0,0,0,0.15),-6px_0_12px_rgba(0,0,0,0.08),6px_0_12px_rgba(0,0,0,0.08)]' 
                          : 'bg-gray-50 dark:bg-gray-900 text-text-secondary hover:text-text-primary hover:bg-gray-100 dark:hover:bg-gray-800 rounded-t-xl'}
                      `}
                    >
                      <span className="text-lg font-semibold">شیفت‌ها</span>
                      {activeTab === 'shifts' && (
                        <div className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-white dark:bg-gray-800"></div>
                      )}
                    </button>
                    <button
                      onClick={() => setActiveTab('orderTypes')}
                      className={`
                        relative flex items-center justify-center gap-3 px-8 py-2.5 text-base font-medium transition-all duration-300 mr-2
                        ${activeTab === 'orderTypes' 
                          ? 'bg-white dark:bg-gray-800 text-primary-600 dark:text-primary-400 border-t-2 border-r-2 border-l-2 border-border-secondary rounded-t-xl shadow-[0_-8px_16px_rgba(0,0,0,0.15),-6px_0_12px_rgba(0,0,0,0.08),6px_0_12px_rgba(0,0,0,0.08)]' 
                          : 'bg-gray-50 dark:bg-gray-900 text-text-secondary hover:text-text-primary hover:bg-gray-100 dark:hover:bg-gray-800 rounded-t-xl'}
                      `}
                    >
                      <span className="text-lg font-semibold">نوع سفارش</span>
                      {activeTab === 'orderTypes' && (
                        <div className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-white dark:bg-gray-800"></div>
                      )}
                    </button>
                    <button
                      onClick={() => setActiveTab('categories')}
                      className={`
                        relative flex items-center justify-center gap-3 px-8 py-2.5 text-base font-medium transition-all duration-300 mr-2
                        ${activeTab === 'categories' 
                          ? 'bg-white dark:bg-gray-800 text-primary-600 dark:text-primary-400 border-t-2 border-r-2 border-l-2 border-border-secondary rounded-t-xl shadow-[0_-8px_16px_rgba(0,0,0,0.15),-6px_0_12px_rgba(0,0,0,0.08),6px_0_12px_rgba(0,0,0,0.08)]' 
                          : 'bg-gray-50 dark:bg-gray-900 text-text-secondary hover:text-text-primary hover:bg-gray-100 dark:hover:bg-gray-800 rounded-t-xl'}
                      `}
                    >
                      <span className="text-lg font-semibold">دسته‌بندی‌ها</span>
                      {activeTab === 'categories' && (
                        <div className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-white dark:bg-gray-800"></div>
                      )}
                    </button>
                  </div>
                </div>

                {/* Tab Content */}
                <div className="animate-enter">
                  {/* Overview Tab */}
                  {activeTab === 'overview' && (
                    <DashboardSectionAnchor sectionId="overall-satisfaction">
                      <OverallSatisfaction 
                        branchId={selectedBranch}
                        timeRange={timeRangeForComponents}
                        onError={handleComponentError}
                      />
                    </DashboardSectionAnchor>
                  )}

                  {/* Shifts Tab */}
                  {activeTab === 'shifts' && (
                    <DashboardSectionAnchor sectionId="shift-satisfaction">
                      <ShiftSatisfaction 
                        branchId={selectedBranch}
                        timeRange={timeRangeForComponents}
                        onError={handleComponentError}
                      />
                    </DashboardSectionAnchor>
                  )}

                  {/* Order Types Tab */}
                  {activeTab === 'orderTypes' && (
                    <DashboardSectionAnchor sectionId="order-type-satisfaction">
                      <OrderTypeSatisfaction 
                        branchId={selectedBranch}
                        timeRange={timeRangeForComponents}
                        onError={handleComponentError}
                      />
                    </DashboardSectionAnchor>
                  )}

                  {/* Categories Tab */}
                  {activeTab === 'categories' && (
                    <DashboardSectionAnchor sectionId="detailed-satisfaction">
                      <DetailedSatisfaction 
                        branchId={selectedBranch}
                        timeRange={timeRangeForComponents}
                        onError={handleComponentError}
                      />
                    </DashboardSectionAnchor>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </LicenseGate>
  );
};

export default SatisfactionDashboard;